import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTelegram } from '../context/TelegramContext';
import { useHaptic } from '../hooks/useHaptic';
import axios from 'axios';
import './PaymentPage.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { webApp } = useTelegram();
  const haptic = useHaptic();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const checkoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const { confirmationToken, transactionId } = location.state || {};
  const cleanupRef = useRef(null);
  const initializedRef = useRef(false); // Флаг для предотвращения повторной инициализации

  useEffect(() => {
    if (!confirmationToken) {
      console.error('No confirmation token provided');
      setError('Токен оплаты не найден');
      setLoading(false);
      return;
    }

    if (initializedRef.current) {
      console.log('Widget already initialized, skipping...');
      return;
    }

    console.log('Confirmation token:', confirmationToken);
    console.log('Checking if YooMoneyCheckoutWidget is available...');
    
    // Функция для инициализации виджета
    const initWidget = () => {
      if (!window.YooMoneyCheckoutWidget) {
        console.error('YooMoneyCheckoutWidget not found on window object');
        console.log('Available on window:', Object.keys(window).filter(k => k.toLowerCase().includes('yoo')));
        return false;
      }
      return true;
    };

    // Даем время скрипту загрузиться
    const checkWidget = () => {
      console.log('Checking widget availability...');
      if (initWidget()) {
        console.log('Widget available, initializing payment page with token:', confirmationToken);
        initializedRef.current = true; // Помечаем как инициализированный
        initializePayment();
      } else {
        // Проверяем еще раз через 500ms
        setTimeout(() => {
          if (!initializedRef.current && initWidget()) {
            console.log('Widget loaded on second try');
            initializedRef.current = true; // Помечаем как инициализированный
            initializePayment();
          }
        }, 500);
      }
    };

    const initializePayment = () => {
      // Сначала показываем форму (убираем loading)
      console.log('Removing loading state to show payment container...');
      setLoading(false);
      
      // Ждем пока элемент появится в DOM
      setTimeout(() => {
        const container = document.getElementById('payment-container');
        if (!container) {
          console.error('payment-container element not found in DOM');
          setError('Ошибка: элемент контейнера не найден');
          return;
        }
        
        console.log('payment-container found in DOM, proceeding with initialization...');
        
        // Отслеживаем события оплаты
        const handleMessage = (event) => {
          console.log('Received postMessage:', event.data);
          
          if (event.data && typeof event.data === 'string') {
            try {
              const data = JSON.parse(event.data);
              console.log('Parsed payment event:', data);
              
            if (data.type === 'success' || data.event === 'success' || data.status === 'succeeded') {
              console.log('Payment success detected from widget event');
              handlePaymentSuccess();
            } else if (data.type === 'error' || data.event === 'error') {
              console.error('Payment error from widget:', data);
              setError('Ошибка оплаты');
              try {
                if (haptic && typeof haptic.notificationOccurred === 'function') {
                  haptic.notificationOccurred('error');
                } else if (webApp && webApp.HapticFeedback) {
                  webApp.HapticFeedback.notificationOccurred('error');
                }
              } catch (e) {}
            }
            } catch (e) {
              // Не JSON, игнорируем
            }
          } else if (event.data && typeof event.data === 'object') {
            // Объект напрямую
            console.log('Received object payment event:', event.data);
            
            if (event.data.type === 'success' || event.data.event === 'success' || event.data.status === 'succeeded') {
              console.log('Payment success detected from widget object event');
              handlePaymentSuccess();
            }
          }
        };

        window.addEventListener('message', handleMessage);

        // Инициализируем виджет
        try {
          console.log('Creating YooMoneyCheckoutWidget instance...');
          checkoutRef.current = new window.YooMoneyCheckoutWidget({
            confirmation_token: confirmationToken,
            customization: {
              colors: {
                control_primary: '#007AFF',
              }
            },
            error_callback: function(error) {
              console.error('YooKassa error callback:', error);
              setError('Ошибка при инициализации оплаты: ' + (error.message || JSON.stringify(error)));
              try {
                if (haptic && typeof haptic.notificationOccurred === 'function') {
                  haptic.notificationOccurred('error');
                } else if (webApp && webApp.HapticFeedback) {
                  webApp.HapticFeedback.notificationOccurred('error');
                }
              } catch (e) {}
            }
          });

          console.log('Rendering widget to payment-container...');
          checkoutRef.current.render('payment-container');
          console.log('Widget rendered successfully!');
          
          // Запускаем polling сразу
          if (transactionId) {
            console.log('Starting payment status polling');
            startPaymentStatusPolling();
          }
          
        } catch (error) {
          console.error('Error initializing payment:', error);
          console.error('Error stack:', error.stack);
          setError('Ошибка инициализации: ' + error.message);
        }

        // Store cleanup function
        cleanupRef.current = () => {
          console.log('Cleaning up payment page...');
          window.removeEventListener('message', handleMessage);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          if (checkoutRef.current && checkoutRef.current.destroy) {
            try {
              checkoutRef.current.destroy();
            } catch (e) {
              console.error('Error destroying widget:', e);
            }
          }
        };
      }, 100); // Даем 100ms для рендеринга DOM
    };

    checkWidget();

    // Return cleanup function
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [confirmationToken]);

  const startPaymentStatusPolling = () => {
    const initData = window.Telegram?.WebApp?.initData;
    
    if (!transactionId || !initData) {
      console.error('Cannot start polling: missing transactionId or initData');
      return;
    }

    if (pollingIntervalRef.current) {
      console.log('Polling already active');
      return;
    }

    console.log('Starting payment status polling for transaction:', transactionId);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        console.log('Checking payment status...');
        const response = await axios.get(
          `${API_URL}/payments/status/${transactionId}`,
          {
            headers: {
              'x-telegram-init-data': initData,
            },
          }
        );
        
        const status = response.data.status;
        console.log('Payment status response:', status);
        
        if (status === 'completed') {
          console.log('Payment completed! Calling handlePaymentSuccess...');
          handlePaymentSuccess();
        } else if (status === 'failed' || status === 'cancelled') {
          console.log('Payment failed or cancelled');
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setError('Оплата отменена');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    }, 2000);

    // Останавливаем через 10 минут
    setTimeout(() => {
      if (pollingIntervalRef.current) {
        console.log('Stopping polling after 10 minutes');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }, 600000);
  };

  const handlePaymentSuccess = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Вибрация успеха
    try {
      if (haptic && typeof haptic.notificationOccurred === 'function') {
        haptic.notificationOccurred('success');
      } else if (webApp && webApp.HapticFeedback) {
        webApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (e) {
      console.log('Haptic feedback not available');
    }
    
    // Показываем успех
    if (webApp) {
      webApp.showAlert('✅ Оплата успешна! Подписка активирована.', () => {
        navigate('/upgrade', { replace: true });
        window.location.reload();
      });
    } else {
      alert('✅ Оплата успешна! Подписка активирована.');
      navigate('/upgrade', { replace: true });
      window.location.reload();
    }
  };

  const handleBack = () => {
    try {
      if (haptic && typeof haptic.impactOccurred === 'function') {
        haptic.impactOccurred('light');
      } else if (webApp && webApp.HapticFeedback) {
        webApp.HapticFeedback.impactOccurred('light');
      }
    } catch (e) {}
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    if (webApp) {
      webApp.showConfirm('Отменить оплату?', (confirmed) => {
        if (confirmed) {
          navigate('/upgrade', { replace: true });
        }
      });
    } else {
      if (window.confirm('Отменить оплату?')) {
        navigate('/upgrade', { replace: true });
      }
    }
  };

  useEffect(() => {
    if (webApp) {
      webApp.BackButton.show();
      webApp.BackButton.onClick(handleBack);

      return () => {
        webApp.BackButton.offClick(handleBack);
        webApp.BackButton.hide();
      };
    }
  }, [webApp]);

  if (error) {
    return (
      <div className="payment-page">
        <div className="payment-error">
          <div className="error-icon">❌</div>
          <h2>Ошибка</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/upgrade')} className="back-button">
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-loading">
          <div className="loader"></div>
          <p>Загрузка формы оплаты...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="payment-header">
        <h1>Оплата подписки</h1>
        <p className="payment-subtitle">Безопасная оплата через YooKassa</p>
      </div>
      
      <div id="payment-container" className="payment-container"></div>
    </div>
  );
}

export default PaymentPage;
